import type {
  AbilityDef,
  AbilityId,
  AuraDef,
  AuraId,
  ContentBundle,
  ContentRegistry,
  EffectSpec,
  EnemyAbility,
  SpecDef,
} from '../contracts';

/** Recursively collect every inline AuraDef reachable from a list of effects. */
export function collectAuras(effects: readonly EffectSpec[] | undefined, out: Map<AuraId, AuraDef>): void {
  if (!effects) return;
  for (const e of effects) {
    switch (e.type) {
      case 'applyAura':
        addAura(e.aura, out);
        break;
      case 'zone':
        if (e.insideAura) addAura(e.insideAura, out);
        collectAuras(e.tick?.effects, out);
        break;
      case 'telegraph':
        collectAuras(e.effects, out);
        break;
      case 'conditional':
        collectAuras(e.then, out);
        collectAuras(e.else, out);
        break;
      default:
        break;
    }
  }
}

function addAura(a: AuraDef, out: Map<AuraId, AuraDef>): void {
  if (out.has(a.id)) return; // first definition wins; reuse ids for identical auras
  out.set(a.id, a);
  collectAuras(a.periodic?.effects, out);
  collectAuras(a.onApply, out);
  collectAuras(a.onRemove, out);
  for (const p of a.procs ?? []) collectAuras(p.effects, out);
}

function collectAbility(ab: AbilityDef, auras: Map<AuraId, AuraDef>): void {
  collectAuras(ab.effects, auras);
  collectAuras(ab.channelStartEffects, auras);
  collectAuras(ab.channelEndEffects, auras); // +lead: rotation rework (F10)
}

/** Index a ContentBundle. Throws on duplicate ids or dangling references. */
export function createRegistry(bundle: ContentBundle): ContentRegistry {
  const abilities = new Map<AbilityId, AbilityDef>();
  const enemy = new Map<AbilityId, EnemyAbility>();
  const auras = new Map<AuraId, AuraDef>();
  const addAbility = (a: AbilityDef): void => {
    const had = abilities.get(a.id);
    // An ability shared by several classes (Dodge) is listed in each: fine while the defs agree.
    if (had && (had === a || JSON.stringify(had) === JSON.stringify(a))) return;
    if (had) throw new Error(`duplicate ability id: ${a.id}`);
    abilities.set(a.id, a);
    collectAbility(a, auras);
  };
  for (const c of bundle.classes) {
    for (const a of c.abilities) addAbility(a);
    for (const p of c.passives ?? []) addAura(p, auras);
    for (const k of Object.keys(c.binds) as (keyof typeof c.binds)[]) {
      if (!c.abilities.some((a) => a.id === c.binds[k])) throw new Error(`class ${c.id}: bind ${k} -> unknown ability ${c.binds[k]}`);
    }
    // +lead: rotation rework (F1): keys 5, 6, G.
    for (const [k, id] of Object.entries(c.extraBinds ?? {})) {
      if (id && !c.abilities.some((a) => a.id === id)) throw new Error(`class ${c.id}: bind ${k} -> unknown ability ${id}`);
    }
  }
  for (const a of bundle.enemyAbilities) {
    addAbility(a);
    enemy.set(a.id, a);
  }
  for (const a of bundle.abilities ?? []) addAbility(a);
  for (const a of bundle.auras ?? []) addAura(a, auras);
  // +specs: index every spec's abilities and auras even while no class carries the spec (Advancement
  // Trial previews, tooltips). A class that does carry it (possibly patched by gear / talents) wins.
  const specs = new Map<string, SpecDef>();
  for (const sp of bundle.specs ?? []) {
    if (specs.has(sp.id)) throw new Error(`duplicate spec id: ${sp.id}`);
    specs.set(sp.id, sp);
    for (const a of sp.abilities) if (!abilities.has(a.id)) addAbility(a);
    for (const p of sp.passives ?? []) addAura(p, auras);
    for (const a of sp.auras ?? []) addAura(a, auras);
  }
  for (const e of bundle.encounters) for (const p of e.phases) collectAuras(p.onEnter, auras);

  const byId = <T extends { id: string }>(arr: readonly T[], what: string): Map<string, T> => {
    const m = new Map<string, T>();
    for (const x of arr) {
      if (m.has(x.id)) throw new Error(`duplicate ${what} id: ${x.id}`);
      m.set(x.id, x);
    }
    return m;
  };
  const classes = byId(bundle.classes, 'class');
  const npcs = byId(bundle.npcs, 'npc');
  const encounters = byId(bundle.encounters, 'encounter');
  const dungeons = byId(bundle.dungeons, 'dungeon');

  for (const n of bundle.npcs) {
    for (const id of n.abilities) if (!enemy.has(id)) throw new Error(`npc ${n.id}: unknown enemy ability ${id}`);
  }

  return {
    bundle,
    abilities,
    auras,
    ability: (id) => abilities.get(id),
    enemyAbility: (id) => enemy.get(id),
    aura: (id) => auras.get(id),
    class: (id) => classes.get(id),
    npc: (id) => npcs.get(id),
    encounter: (id) => encounters.get(id),
    dungeon: (id) => dungeons.get(id),
    spec: (id) => specs.get(id),
    specs: [...specs.values()],
    npcBaseHp: (id) => {
      const n = npcs.get(id);
      if (!n) return 0;
      return Math.round(bundle.tierHp[n.tier] * n.hpMult);
    },
  };
}
